// Stock counts (plan §9.5; D-049): each count a snapshot at its counted_at — number, where
// and what it covered, blind or not, when and who, how many rows differed and what that is
// worth, and its status (applied, superseded, waiting for approval, approved, reversed).
// Filters: trading days, location, one product, and "needs approval".

import type { CountSummary } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import {
  Badge,
  Button,
  cn,
  createColumnHelper,
  DataTable,
  EmptyState,
  Money,
  PageHeader,
  StatusBadge,
  type ColumnDef,
} from '@enp/ui';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, ClipboardList, EyeOff, Flag, SearchX, X } from 'lucide-react';
import { useMemo } from 'react';
import { meQueryOptions } from '../../../../auth/session.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { plural, SignedMoney } from '../../../../stock-control/parts.js';
import {
  countsQuery,
  countsSearchSchema,
  type CountsSearch,
} from '../../../../stock-control/queries.js';
import { COUNT_SCOPE_LABEL, countStatus } from '../../../../stock-control/words.js';
import {
  DayRange,
  FilterSelect,
  LoadMoreFooter,
  StockViews,
} from '../../../../stock/components.js';
import { accountToday } from '../../../../stock/labels.js';
import { clean, locationsQuery, lotsQuery } from '../../../../stock/queries.js';

export const Route = createFileRoute('/_app/stock/counts/')({
  validateSearch: countsSearchSchema,
  component: CountsPage,
});

const TABLE_LABEL = 'Stock counts';

function columns(): ColumnDef<CountSummary, unknown>[] {
  const col = createColumnHelper<CountSummary>();
  return [
    col.display({
      id: 'number',
      header: 'Count',
      meta: { width: 'min-w-40' },
      cell: ({ row }) => {
        const c = row.original;
        return (
          <span className="flex flex-col items-start gap-1 leading-tight">
            <Link
              to="/stock/counts/$countId"
              params={{ countId: c.id }}
              tabIndex={-1}
              className="whitespace-nowrap font-mono text-sm font-medium text-fg hover:underline"
            >
              {c.number}
            </Link>
            {c.blind && (
              <Badge tone="neutral">
                <EyeOff aria-hidden />
                Blind
              </Badge>
            )}
          </span>
        );
      },
    }),
    col.display({
      id: 'where',
      header: 'Where',
      cell: ({ row }) => {
        const c = row.original;
        return (
          <span className="flex flex-col leading-tight">
            <span className="whitespace-nowrap text-fg">{c.location?.name ?? c.branch.name}</span>
            {/* One line, cut short with the whole text on hover, so a row stays two lines
                tall at 1280 (UI8B-4). */}
            <span
              className="block max-w-28 truncate text-xs text-muted min-[1400px]:max-w-56"
              title={`${COUNT_SCOPE_LABEL[c.scope]}${c.location ? ` · ${c.branch.name}` : ''}`}
            >
              {COUNT_SCOPE_LABEL[c.scope]}
              {c.location ? ` · ${c.branch.name}` : ''}
            </span>
          </span>
        );
      },
    }),
    col.display({
      id: 'when',
      header: 'Counted',
      cell: ({ row }) => (
        <span className="flex flex-col whitespace-nowrap leading-tight">
          <AccountDate value={row.original.counted_at} withTime className="text-fg" />
          <span className="text-xs text-muted">
            {row.original.counted_by?.name ?? 'Unknown person'}
          </span>
        </span>
      ),
    }),
    col.display({
      id: 'rows',
      header: 'Rows',
      cell: ({ row }) => {
        const c = row.original;
        return (
          <span className="flex flex-col whitespace-nowrap leading-tight">
            <span className="text-fg">{plural(c.line_count, 'row', 'rows')}</span>
            <span className={cn('text-xs', c.variance_count > 0 ? 'text-fg' : 'text-muted')}>
              {c.variance_count === 0 && c.noted_variance_count > 0
                ? // Superseded rows: the difference was noted, never booked (UI4A-1).
                  `${formatCount(c.noted_variance_count)} differed · noted, not booked`
                : c.variance_count === 0 && c.reversed_variance_count === 0
                  ? 'None differed'
                  : `${formatCount(c.variance_count)} differed`}
            </span>
            {c.reversed_variance_count > 0 && (
              // Booked, then undone by a rejection (B-087): not in the figures.
              <span className="text-xs text-muted">
                {formatCount(c.reversed_variance_count)} reversed on rejection
              </span>
            )}
          </span>
        );
      },
    }),
    col.display({
      id: 'value',
      header: 'Variance value',
      meta: { align: 'right' },
      cell: ({ row }) => {
        const c = row.original;
        if (c.variance_count === 0) {
          return (
            <span className="whitespace-nowrap text-sm text-muted">
              {c.reversed_variance_count > 0
                ? 'Reversed, not booked'
                : c.noted_variance_count > 0
                  ? 'Noted, not booked'
                  : 'No difference'}
            </span>
          );
        }
        return (
          <span className="flex flex-col items-end leading-tight">
            <SignedMoney cents={c.net_value_cents} className="font-semibold text-fg" />
            <span className="whitespace-nowrap text-xs text-muted">
              {c.gross_value_cents !== Math.abs(c.net_value_cents) && (
                <>
                  <Money cents={c.gross_value_cents} /> moved ·{' '}
                </>
              )}
              {c.uncosted_variance_count > 0 ? `${c.uncosted_variance_count} without cost` : 'net'}
            </span>
            {c.reversed_variance_count > 0 && (
              <span className="whitespace-nowrap text-xs text-muted">
                <SignedMoney cents={c.reversed_net_value_cents} /> reversed
              </span>
            )}
          </span>
        );
      },
    }),
    col.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const c = row.original;
        const s = countStatus(c);
        return (
          <span className="flex flex-col items-start gap-1">
            <StatusBadge status={s.status}>{s.label}</StatusBadge>
            {c.needs_approval && c.approval?.status !== 'requested' && (
              <span className="flex items-center gap-1 text-xs text-muted">
                <Flag aria-hidden className="size-3" />
                Was above the limit
              </span>
            )}
          </span>
        );
      },
    }),
    col.display({
      id: 'open',
      header: () => <span className="sr-only">Open</span>,
      // The whole row opens the count; below 1400 px the arrow gives its room to Status (UI8B-4).
      meta: { width: 'w-8', className: 'hidden min-[1400px]:table-cell' },
      cell: () => <ChevronRight aria-hidden className="size-4 text-muted" />,
    }),
  ];
}

/**
 * What the counts listed are worth together, in rand (UI4A-8): net (gains less losses)
 * and moved, at standard cost; noted-only differences (superseded) and differences a
 * rejection reversed (B-087) are not in it.
 */
function PeriodSummary({ items, complete }: { items: CountSummary[]; complete: boolean }) {
  const net = items.reduce((s, c) => s + c.net_value_cents, 0);
  const moved = items.reduce((s, c) => s + c.gross_value_cents, 0);
  const uncosted = items.reduce((s, c) => s + c.uncosted_variance_count, 0);
  const reversed = items.reduce((s, c) => s + c.reversed_variance_count, 0);
  return (
    <p
      className="mb-3 flex flex-wrap items-baseline gap-x-2 text-sm text-muted"
      data-testid="counts-summary"
    >
      <span>
        {complete ? 'These' : 'The'} {plural(items.length, 'count', 'counts')}
        {complete ? '' : ' loaded so far'}:
      </span>
      <span>
        net <SignedMoney cents={net} lossTone className="font-semibold text-fg" />
      </span>
      <span aria-hidden>·</span>
      <span>
        <Money cents={moved} className="font-semibold text-fg" /> moved
      </span>
      {uncosted > 0 && (
        <>
          <span aria-hidden>·</span>
          <span>{plural(uncosted, 'row', 'rows')} without a cost</span>
        </>
      )}
      {reversed > 0 && (
        <>
          <span aria-hidden>·</span>
          <span>{plural(reversed, 'row', 'rows')} reversed on rejection, not in these</span>
        </>
      )}
    </p>
  );
}

function CountsPage() {
  useDocumentTitle('Stock counts');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const setSearch = (patch: Partial<CountsSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });

  const locations = useQuery(locationsQuery());
  // The product filter comes from a product's stock page: its name, for the chip.
  const product = useQuery({
    ...lotsQuery(search.product ?? ''),
    enabled: Boolean(search.product),
  });
  const list = useInfiniteQuery({ ...countsQuery(search), placeholderData: keepPreviousData });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const filtered = Boolean(
    search.from || search.to || search.location || search.product || search.flagged,
  );
  const cols = columns();

  const locationOptions = (locations.data?.items ?? []).map((l) => ({
    value: l.id,
    label: l.name,
    group: l.branch.name,
  }));

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title="No counts match"
      description="No stock count fits these filters. Try other days, or clear the filters."
      action={
        <Button
          variant="secondary"
          icon={<X aria-hidden />}
          onClick={() => void navigate({ search: {} })}
        >
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={ClipboardList}
      title="No stock counts yet"
      description="Counts are taken at the Stock take station: pick a location, count what’s there, and the differences are corrected in the stock ledger. They appear here with what they changed."
    />
  );

  return (
    <>
      <PageHeader
        title="Stock"
        description="Every stock count: what the shelf held against the system at that moment, and what the differences are worth."
      />
      <StockViews current="counts" />

      <div
        className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2"
        role="search"
        aria-label="Count filters"
      >
        <DayRange
          idPrefix="counts"
          from={search.from}
          to={search.to}
          today={today}
          onChange={(r) => setSearch(r)}
        />
        <FilterSelect
          label="Location"
          value={search.location}
          allLabel="All locations"
          options={locationOptions}
          onChange={(v) => setSearch({ location: v })}
        />
        <Button
          variant="secondary"
          aria-pressed={Boolean(search.flagged)}
          className={cn(search.flagged && 'border-primary/60 bg-primary-subtle text-primary-text')}
          icon={<Flag aria-hidden />}
          onClick={() => setSearch({ flagged: search.flagged ? undefined : true })}
        >
          Above the limit
        </Button>
        {search.product && (
          <Badge tone="neutral" className="h-control-sm gap-1.5 pr-1">
            {product.data?.product.name ?? 'One product'}
            <button
              type="button"
              aria-label="Show every product"
              onClick={() => setSearch({ product: undefined })}
              className="rounded-sm p-0.5 hover:bg-surface-hover"
            >
              <X aria-hidden className="size-3.5" />
            </button>
          </Badge>
        )}
        {filtered && (
          <Button
            variant="ghost"
            icon={<X aria-hidden />}
            onClick={() => void navigate({ search: {} })}
          >
            Clear
          </Button>
        )}
      </div>

      {!list.isPending && items.length > 0 && (
        <PeriodSummary items={items} complete={!list.hasNextPage} />
      )}

      {list.isError && !list.data ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The counts"
          onRetry={() => void list.refetch()}
        />
      ) : (
        <DataTable
          label={TABLE_LABEL}
          columns={cols}
          data={items}
          loading={list.isPending}
          empty={empty}
          filterable={false}
          getRowId={(c) => c.id}
          onRowActivate={(c) =>
            void navigate({ to: '/stock/counts/$countId', params: { countId: c.id } })
          }
          footer={
            !list.isPending && items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                noun={['count', 'counts']}
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
          className="xl:max-h-[calc(100dvh-20rem)]"
        />
      )}
    </>
  );
}
